// eslint-disable-next-line node/no-unpublished-import
import type {AddEthereumChainParameter} from '@web3-react/types';
import tokenDeployments from '@kei.fi/token-contracts/deployments.json';

export const ENVIRONMENT: 'develop' | 'staging' | 'production' | 'local' =
  (() => {
    const env = process.env.NEXT_PUBLIC_ENV?.toLowerCase();
    switch (env) {
      case 'production':
      case 'prod':
        return 'production';
      case 'staging':
      case 'stage':
        return 'staging';
      case 'development':
      case 'develop':
      case 'dev':
        return 'develop';
      default:
        return 'local';
    }
  })();

export const PRIMARY_CHAIN =
  ENVIRONMENT === 'production' || ENVIRONMENT === 'staging' ? 42161 : 11155111;
export const IS_DEV = ENVIRONMENT === 'develop' || ENVIRONMENT === 'local';

const Boolean = (value: string | undefined): value is string =>
  value !== undefined;

const ETH: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Ether',
  symbol: 'ETH',
  decimals: 18,
};

const gETH: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Görli Ether',
  symbol: 'gETH',
  decimals: 18,
};

const MATIC: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Matic',
  symbol: 'MATIC',
  decimals: 18,
};

const CELO: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Celo',
  symbol: 'CELO',
  decimals: 18,
};

interface BasicChainInformation {
  urls: string[];
  name: string;
  nativeCurrency: AddEthereumChainParameter['nativeCurrency'];
}

interface ExtendedChainInformation extends BasicChainInformation {
  blockExplorerUrls: AddEthereumChainParameter['blockExplorerUrls'];
}

function isExtendedChainInformation(
  chainInformation: BasicChainInformation | ExtendedChainInformation
): chainInformation is ExtendedChainInformation {
  return !!(chainInformation as ExtendedChainInformation).blockExplorerUrls;
}

export function getAddChainParameters(
  chainId: number
): AddEthereumChainParameter | number {
  const chainInformation = CHAINS[chainId];
  if (isExtendedChainInformation(chainInformation)) {
    return {
      chainId,
      chainName: chainInformation.name,
      nativeCurrency: chainInformation.nativeCurrency,
      rpcUrls: chainInformation.urls,
      blockExplorerUrls: chainInformation.blockExplorerUrls,
    };
  } else {
    return chainId;
  }
}

const getInfuraUrlFor = (network: string) =>
  process.env.NEXT_PUBLIC_INFURA_API_KEY
    ? `https://${network}.infura.io/v3/${process.env.NEXT_PUBLIC_INFURA_API_KEY}`
    : undefined;
const getAlchemyUrlFor = (network: string) =>
  process.env.REACT_APP_ALCHEMY_API_KEY
    ? `https://${network}.alchemyapi.io/v2/${process.env.REACT_APP_ALCHEMY_API_KEY}`
    : undefined;

export type ChainInformation = BasicChainInformation | ExtendedChainInformation;

export type ChainConfig = {
  [chainId: number]: ChainInformation;
};

export const MAINNET_CHAINS: ChainConfig = {
  1: {
    urls: [
      getInfuraUrlFor('mainnet'),
      getAlchemyUrlFor('eth-mainnet'),
      'https://cloudflare-eth.com',
    ].filter(Boolean),
    name: 'Mainnet',
    nativeCurrency: ETH,
  },
  10: {
    urls: [
      getInfuraUrlFor('optimism-mainnet'),
      'https://mainnet.optimism.io',
    ].filter(Boolean),
    name: 'Optimism',
    nativeCurrency: ETH,
    blockExplorerUrls: ['https://optimistic.etherscan.io'],
  },
  42161: {
    urls: [
      getInfuraUrlFor('arbitrum-mainnet'),
      'https://arb1.arbitrum.io/rpc',
    ].filter(Boolean),
    name: 'Arbitrum One',
    nativeCurrency: ETH,
    blockExplorerUrls: ['https://arbiscan.io'],
  },
  137: {
    urls: [
      getInfuraUrlFor('polygon-mainnet'),
      'https://polygon-rpc.com',
    ].filter(Boolean),
    name: 'Polygon Mainnet',
    nativeCurrency: MATIC,
    blockExplorerUrls: ['https://polygonscan.com'],
  },
  42220: {
    urls: ['https://forno.celo.org'],
    name: 'Celo',
    nativeCurrency: CELO,
    blockExplorerUrls: ['https://explorer.celo.org'],
  },
};

export const TESTNET_CHAINS: ChainConfig = {
  5: {
    urls: [getInfuraUrlFor('goerli')].filter(Boolean),
    name: 'Görli',
    nativeCurrency: gETH,
  },
  420: {
    urls: [
      getInfuraUrlFor('optimism-goerli'),
      'https://goerli.optimism.io',
    ].filter(Boolean),
    name: 'Optimism Goerli',
    nativeCurrency: ETH,
    blockExplorerUrls: ['https://goerli-explorer.optimism.io'],
  },
  421613: {
    urls: [
      getInfuraUrlFor('arbitrum-goerli'),
      'https://goerli-rollup.arbitrum.io/rpc',
    ].filter(Boolean),
    name: 'Arbitrum Goerli',
    nativeCurrency: ETH,
    blockExplorerUrls: ['https://testnet.arbiscan.io'],
  },
  80001: {
    urls: [getInfuraUrlFor('polygon-mumbai')].filter(Boolean),
    name: 'Polygon Mumbai',
    nativeCurrency: MATIC,
    blockExplorerUrls: ['https://mumbai.polygonscan.com'],
  },
  44787: {
    urls: ['https://alfajores-forno.celo-testnet.org'],
    name: 'Celo Alfajores',
    nativeCurrency: CELO,
    blockExplorerUrls: ['https://alfajores-blockscout.celo-testnet.org'],
  },
  11155111: {
    urls: [getInfuraUrlFor('sepolia')].filter(Boolean),
    name: 'Sepolia',
    nativeCurrency: ETH,
    blockExplorerUrls: ['https://sepolia.etherscan.io'],
  },
};

export const CHAINS: ChainConfig = {
  ...MAINNET_CHAINS,
  ...TESTNET_CHAINS,
};

export const CHAIN_IDS = Object.keys(CHAINS).map(Number);

export const URLS: {[chainId: number]: string[]} = Object.keys(CHAINS).reduce<{
  [chainId: number]: string[];
}>((accumulator, chainId) => {
  const validURLs: string[] = CHAINS[Number(chainId)].urls;

  if (validURLs.length) {
    accumulator[Number(chainId)] = validURLs;
  }

  return accumulator;
}, {});

export const TOKEN_ADDRESS =
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  tokenDeployments[PRIMARY_CHAIN]?.KEIToken?.toLowerCase() ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  tokenDeployments[PRIMARY_CHAIN]?.KEITokenL2?.toLowerCase();

export const ETH_ADDRESS = '0x'.padEnd(42, 'e');
